import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import TeamAlertService from 'src/service/TeamAlertService'
import ToastComponent from 'src/components/common/TaostComponent'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
  short_description: Yup.string()
    .required('Short description is required')
    .max(150, '150 Character Limit is allowed.'),
})

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  const formik = useFormik({
    initialValues: {
      title: '',
      short_description: '',
      description: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('short_description', data.short_description)
      formData.append('description', data.description)

      setLoader(true)
      TeamAlertService.saveTeamAlert(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const resetFields = (actions) => {
    actions.resetForm({
      values: { title: '', short_description: '', description: '' },
    })
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Alert</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Write an alert. It won&apos;t reach members until you send it from the list.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="title">Title *</CFormLabel>
            <CFormInput
              id="title"
              name="title"
              placeholder="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.errors.title && formik.touched.title)}
            />
            {formik.errors.title && formik.touched.title && (
              <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="short_description">Short Description (For App) *</CFormLabel>
            <CFormTextarea
              id="short_description"
              name="short_description"
              rows={2}
              value={formik.values.short_description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.errors.short_description && formik.touched.short_description)}
            />
            {formik.errors.short_description && formik.touched.short_description && (
              <CFormFeedback invalid>{formik.errors.short_description}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="description">Long Description (For Web)</CFormLabel>
            <div className="ta-editor">
              <ReactQuill
                theme="snow"
                ref={quillRef}
                value={formik.values.description}
                onChange={(content) => formik.setFieldValue('description', content)}
                modules={modules}
              />
            </div>
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Alert
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
